import React, { useState, useEffect, useCallback, useRef } from "react";

import { useParams } from 'react-router-dom';
import {
  Box,
  FormControl,
  FormLabel,
  Input,
  Text,
  Heading,
  Button,
  useToast,
  useColorModeValue,
  Flex,
  VStack,
  Tbody,
  Table,
  Tr,
  Td,
  Th,
  Thead,
  IconButton,
  HStack,
  Badge,
  Checkbox,

} from "@chakra-ui/react";
import axios from "axios";
import { FiTrash, FiCheck, FiX, FiEdit } from "react-icons/fi";
import { jwtDecode } from "jwt-decode";
import { Exam,QuestionSet, ExamQsetMapping, QuestionSetCategory, Batch, ExamBatch,ExamAttemptScoreStatusByTutor } from '../../../Platform/Common/types';
import { addMinutes } from 'date-fns'; // You can also manually add mins without this
import { Select as ChakraSelect } from '@chakra-ui/react';
import ReactSelect from 'react-select';
import { useNavigate } from "react-router-dom";
import ExamAttemptScoringStatus from "../../../Platform/Components/ExamAttemptScoringStatus";
import { EditIcon, CheckCircleIcon } from "@chakra-ui/icons";
import { fmtLocal, utcToInputValue, inputValueToUtc, parseUtc, asUtcDate, toBackendISO } from '../../../utils/dates';
import { useTenant } from "../../../Platform/Routers/TenantContextProvider";

interface TokenPayload {
    sub: string;
    role_id: number;
    tenant_id: number;
    exp?: number;
  }

  const ReviewB2CExam= () => {
    // ────────────────────────────────────────────────────────────────────────────
    // Theme & routing hooks
    // ────────────────────────────────────────────────────────────────────────────
    const buttonBg = useColorModeValue(
      'linear-gradient(to right, blue.50, blue.100)',
      'linear-gradient(to right, blue.600, blue.700)'
    );
    const buttonHoverBg = useColorModeValue('blue.200', 'blue.500');
    const cardbg = useColorModeValue("gray.50", "gray.700");

    const { tenant, loading, error } = useTenant();
  
    const { exam_id } = useParams<{ exam_id: string }>();
    const [examId, setExamId] = useState<number>(Number(exam_id));
    const navigate = useNavigate();
    const toast = useToast();
  
    // ────────────────────────────────────────────────────────────────────────────
    // State: core exam, tenant, question-sets, mappings, batches
    // ────────────────────────────────────────────────────────────────────────────
    const [selectedTenant, setSelectedTenant] = useState<number | null>(null);
  
    const [exam, setExam] = useState<Exam>({
      exam_id: 0,
      tenant_id: 0,
      title: '',
      description: '',
      status: "DRAFT",
      published_at: new Date(),
      passing_percentage : 40,
      max_view_limit : 3,
      result_challenge_days : 7,
      instant_result_enabled :false,

      manual_scoring_pending_attempts_count : 0,
      auto_scoring_pending_attempts_count :  0,
      results_auto_publish :false,
      start_time: new Date(),
      end_time: new Date(),

        // 🔽 Add these three new fields
      results_published_on: undefined,
      results_finalized_on: undefined,
      results_finalized_by: undefined,
      is_analytics_generated : false,

      created_at: new Date(),
      updated_at: new Date(),
    });
  
    const [questionSets, setQuestionSets] = useState<QuestionSet[]>([]);
    const [questionSetCategories, setQuestionSetCategories] = useState<QuestionSetCategory[]>([]);
  
    type EditableMapping = ExamQsetMapping & { isDraft?: boolean };

    const [mappings, setMappings] = useState<EditableMapping[]>([]);
    const [isMappingEdited, setIsMappingEdited] = useState(false);
    const [isExamEdited, setIsExamEdited] = useState(false);
  
    const [batches, setBatches] = useState<Batch[]>([]);
    const [examBatchMapping, setExamBatchMapping] = useState<ExamBatch[]>([]);
    const [batchMappedList, setBatchMappedList] = useState<Batch[]>([]);
    const [batchSelectedIds, setBatchSelectedIds] = useState<number[]>([]);
    const [batchIsMappingEdited, setBatchIsMappingEdited] = useState(false);

    const [examAttemptScoreStatusByTutor, setExamAttemptScoreStatusByTutor] = useState<ExamAttemptScoreStatusByTutor[]>([]);
    
  
    // ────────────────────────────────────────────────────────────────────────────
    // Mode & button logic
    // ────────────────────────────────────────────────────────────────────────────
    const [isEditing, setIsEditing] = useState(false);
    const isCreating = examId === 0;
    const isViewMode = !isEditing;
  
    const isDraft     = exam.status === "DRAFT";
    const isPublished = exam.status === "PUBLISHED";
    const isOngoing   = exam.status === "ONGOING";
    const isCompleted = exam.status === "COMPLETED";
    const isInScoring = exam.status === "SCORING_IN_PROGRESS";
    const isFullyScored = exam.status === "FULLY_SCORED";
    const isResultsPublished = exam.status === "RESULTS_PUBLISHED";
    const isResultsFinalized = exam.status === "RESULTS_FINALIZED";
    
  
    const showActionButtons    = !isCreating;
    const showSaveButton       = isCreating || !isViewMode;
    const isUpdateButtonEnabled   = showActionButtons && isViewMode && (isDraft || isPublished);
    const isPublishButtonEnabled  = showActionButtons && isViewMode && isDraft;
    const isCancelButtonEnabled   = showActionButtons && isViewMode && isPublished;
    const isTerminateButtonEnabled= showActionButtons && isViewMode && isOngoing;
    const isScoringButtonEnabled= showActionButtons && isViewMode && isCompleted;
    const isExamAttemptStatusButtonEnabled= showActionButtons && isViewMode && isInScoring;
    const isPublishResultsButtonEnabled= showActionButtons && isViewMode && isFullyScored;
    const isGenerateAnalyticsButtonEnabled = showActionButtons && isViewMode && isFullyScored && (!exam.is_analytics_generated)

  //  const isFinalizeResultsButtonEnabled= showActionButtons && isViewMode && isResultsPublished;
  const isFinalizeResultsButtonEnabled =
  showActionButtons &&
  isViewMode &&
  isResultsPublished &&
  exam.results_published_on !== undefined &&
  Date.now() >= parseUtc(exam.results_published_on).getTime() + exam.result_challenge_days * 86_400_000;   // 24 h in ms
 // new Date() >= new Date(new Date(exam.results_published_on).getTime() + exam.result_challenge_days * 24 * 60 * 60 * 1000);

  
    // ────────────────────────────────────────────────────────────────────────────
    // Utils: decode tenant from JWT
    // ────────────────────────────────────────────────────────────────────────────
    const getTenantIdFromToken = (token: string): number | null => {
      try {
        const decoded = jwtDecode<TokenPayload>(token);
        return decoded.tenant_id || null;
      } catch {
        return null;
      }
    };

        /* ── put these once, e.g. in the same file or utils ─────────────────────── */
    const normaliseExam = (e: Exam): Exam => ({
      ...e,
      start_time:            parseUtc(e.start_time),
      end_time:              parseUtc(e.end_time),
      published_at:          e.published_at          && parseUtc(e.published_at),
      results_published_on:  e.results_published_on  && parseUtc(e.results_published_on),
      results_finalized_on:  e.results_finalized_on  && parseUtc(e.results_finalized_on),
    });

    const normaliseMappings = (arr: ExamQsetMapping[]): ExamQsetMapping[] =>
      arr.map(m => ({ ...m, start_time: parseUtc(m.start_time) }));
      
    // ────────────────────────────────────────────────────────────────────────────
    // API fetchers
    // ────────────────────────────────────────────────────────────────────────────
    const fetchExam = async () => {
      const api = process.env.REACT_APP_API_URL || "https://default-url.com";
      const { data } = await axios.get<Exam>(`${api}/exam/${exam_id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setExam(normaliseExam(data));                        // <- Date objects in state
    };
      
    const fetchQuestionSets = async (tenantId: number) => {
      const api = process.env.REACT_APP_TENANTS_API_URL || 'https://default-url.com';
      const { data } = await axios.get<QuestionSet[]>(`${api}/${tenantId}/qsets`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setQuestionSets(data);
    };
  
    const fetchQuestionSetCategories = async (tenantId: number) => {
      const api = process.env.REACT_APP_TENANTS_API_URL || 'https://default-url.com';
      const { data } = await axios.get<QuestionSetCategory[]>(`${api}/${tenantId}/qset-categories`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setQuestionSetCategories(data);
    };
  
    const fetchExamQsetMappings = async () => {
      const api = process.env.REACT_APP_API_URL || 'https://default-url.com';
      const { data } = await axios.get<ExamQsetMapping[]>(`${api}/exam-mappings/${exam_id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setMappings(normaliseMappings(data));   
    };
  
    const fetchBatches = async (tenantId: number) => {
      const api = process.env.REACT_APP_TENANTS_API_URL || 'https://default-url.com';
      const { data } = await axios.get<Batch[]>(`${api}/${tenantId}/batch-details`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setBatches(data);
    };
  
    const fetchExamBatchMappings = async () => {
      const api = process.env.REACT_APP_API_URL || 'https://default-url.com';
      const { data } = await axios.get<ExamBatch[]>(`${api}/exam-batch-mappings/${exam_id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setExamBatchMapping(data);
    };
  
    // ────────────────────────────────────────────────────────────────────────────
    // Derived state effects
    // ────────────────────────────────────────────────────────────────────────────

    const loadAllData = useCallback(async () => {
        const token = localStorage.getItem("token");
        if (!token) return;
      
        // 1) tenant
        const tenantId = getTenantIdFromToken(token) ?? 0;
        setSelectedTenant(tenantId);
        setExam(e => ({ ...e, tenant_id: tenantId }));
      
        // 2) if editing an existing exam
        if (Number(exam_id) > 0) {
          await fetchExam();
          await fetchExamQsetMappings();
          await fetchExamBatchMappings();
          setExamId(Number(exam_id));
        }
      
        // 3) fetch all tenant‐scoped lookups
        if (tenantId) {
          await Promise.all([
            fetchQuestionSets(tenantId),
            fetchQuestionSetCategories(tenantId),
            fetchBatches(tenantId),
          ]);
        }
      
        // 4) reset any “edited” flags
        setIsEditing(false);
        setIsMappingEdited(false);
        setBatchIsMappingEdited(false);
        setIsExamEdited(false);
      }, [
        exam_id,
        fetchExam,
        fetchExamQsetMappings,
        fetchExamBatchMappings,
        fetchQuestionSets,
        fetchQuestionSetCategories,
        fetchBatches,
      ]);

    // 1) On load — get tenant, then fetch everything
    useEffect(() => {
      (async () => {
        const token = localStorage.getItem("token");
        if (!token) return;
  
        const tenantId = getTenantIdFromToken(token) ?? 0;
        setSelectedTenant(tenantId);
        setExam(e => ({ ...e, tenant_id: tenantId }));
  
        if (Number(exam_id) > 0) {
          await fetchExam();
          await fetchExamQsetMappings();
          await fetchExamBatchMappings();
          setExamId(Number(exam_id));
        }
        if (tenantId) {
          await fetchQuestionSets(tenantId);
          await fetchQuestionSetCategories(tenantId);
          await fetchBatches(tenantId);
        }
      })();
    }, [exam_id]);
  
    // 2) Recompute exam start/end from mappings
/*     useEffect(() => {
      if (!mappings.length || !questionSets.length) return;
      const sorted = [...mappings].sort((a, b) => a.sequence - b.sequence);
      const firstStart = new Date(sorted[0].start_time);
      const last = sorted[sorted.length - 1];
      const qs = questionSets.find(q => q.question_set_id === last.question_set_id);
      const endTime = new Date(new Date(last.start_time).getTime() + (qs?.duration || 0) * 60000);
      setExam(e => ({ ...e, start_time: firstStart, end_time: endTime }));
    }, [mappings, questionSets]); */

    // 2) Recompute exam start/end from mappings ────────────────────────────────
    /*   useEffect(() => {
        if (!mappings.length || !questionSets.length) return;

        // sort once, treat every mapping start_time as UTC
        const sorted   = [...mappings].sort((a, b) => a.sequence - b.sequence);
        const first    = asUtcDate(sorted[0].start_time);         // ← UTC Date
        const lastMap  = sorted[sorted.length - 1];
        const lastQset = questionSets.find(
          q => q.question_set_id === lastMap.question_set_id
        );

        const lastStart = asUtcDate(lastMap.start_time);          // ← UTC Date
        const endTime   = new Date(
          lastStart.getTime() + (lastQset?.duration ?? 0) * 60_000
        );

        console.log("First Start Time", first);
        console.log("First end Time", endTime);

        setExam(prev => ({
          ...prev,
          start_time: first,     // still a Date (UTC)
          end_time  : endTime,   // still a Date (UTC)
        }));

        console.log("exam start time", exam.start_time);
        console.log("exam end time", exam.end_time);
      }, [mappings, questionSets]); */

      useEffect(() => {
          if (!mappings.length || !questionSets.length) return;

          const sorted   = [...mappings].sort((a, b) => a.sequence - b.sequence);

          /* first mapping’s start time (UTC) */
          const first    = parseUtc(sorted[0].start_time);     // ✅ correct moment

          /* last mapping + its question-set duration */
          const lastMap  = sorted[sorted.length - 1];
          const lastQset = questionSets.find(
            q => q.question_set_id === lastMap.question_set_id,
          );

          const lastStart = parseUtc(lastMap.start_time);      // ✅ correct moment
          const endTime   = new Date(
            lastStart.getTime() + (lastQset?.duration ?? 0) * 60_000,
          );

          setExam(prev => ({
            ...prev,
            start_time: first,      // still a Date (but now the right one)
            end_time  : endTime,
          }));
        }, [mappings, questionSets]);
  
    // 3) Build full batch list from mapping IDs
    useEffect(() => {
      if (!examBatchMapping.length || !batches.length) return;
      setBatchMappedList(
        examBatchMapping
          .map(m => batches.find(b => b.id === m.batch_id))
          .filter((b): b is Batch => !!b)
      );
    }, [examBatchMapping, batches]);


    const hasFetchedStatus = useRef(false);

    useEffect(() => {
      if (
        exam &&
        (exam.status === "SCORING_IN_PROGRESS" || exam.status === "FULLY_SCORED" || exam.status === "RESULTS_PUBLISHED" )&&
        !hasFetchedStatus.current
      ) {
        fetchExamAttemptStatusByTutor();
        hasFetchedStatus.current = true;
      }
    }, [exam]);
  

    // ────────────────────────────────────────────────────────────────────────────
    // Mapping helpers
    // ────────────────────────────────────────────────────────────────────────────
    // ────────────────────────────────────────────────────────────────────────────
    //  Extend your mapping type locally to carry a draft‐flag
    // ────────────────────────────────────────────────────────────────────────────

    // helper to know if a draft row exists
    const hasDraftMappingRow = () => mappings.some(m => m.isDraft);

    const [mappingBackups, setMappingBackups] = useState<Record<number, ExamQsetMapping>>({});

    // ────────────────────────────────────────────────────────────────────────────
    //  Add / confirm / cancel mapping row
    // ────────────────────────────────────────────────────────────────────────────

    // 1) add a *draft* row (only if none exists)
 /*    const addMapping = () => {
        if (hasDraftMappingRow()) return;  // only one draft at a time
      
        // compute nextStartTime exactly as before…
        let nextStart = new Date();
        if (mappings.length) {
          const sorted = [...mappings].sort((a,b)=>a.sequence - b.sequence);
          const last = sorted[sorted.length - 1];
          const qs = questionSets.find(q => q.question_set_id === last.question_set_id);
          nextStart = new Date(
            new Date(last.start_time).getTime() + (qs?.duration||0) * 60000
          );
        }
      
        setMappings(prev => [
          ...prev,
          {
            exam_id: Number(exam_id),
            sequence: prev.length + 1,
            question_set_id: 0,
            start_time: nextStart,
            isDraft: true,      // ← mark it draft
          }
        ]);
        setIsMappingEdited(true);
      };
       */

      const addMapping = () => {
            if (hasDraftMappingRow()) return;

            let nextStart = new Date();                      // already UTC
            if (mappings.length) {
              const sorted   = [...mappings].sort((a, b) => a.sequence - b.sequence);
              const lastMap  = sorted[sorted.length - 1];
              const lastQset = questionSets.find(
                q => q.question_set_id === lastMap.question_set_id
              );

              const lastStart = asUtcDate(lastMap.start_time);       // ← UTC
              nextStart = new Date(
                lastStart.getTime() + (lastQset?.duration ?? 0) * 60_000
              );
            }

            setMappings(prev => [
              ...prev,
              {
                exam_id          : Number(exam_id),
                sequence         : prev.length + 1,
                question_set_id  : 0,
                start_time       : nextStart,   // UTC Date
                isDraft          : true,
              },
            ]);
            setIsMappingEdited(true);
          };
      // -----------------------------------------------------------------------------
       // 5) Turn an **existing** row into draft/edit mode
      // -----------------------------------------------------------------------------
      const editMapping = (idx: number) => {
        if (hasDraftMappingRow()) return;  // lock while another is open
        // snapshot original
        setMappingBackups(b => ({ ...b, [idx]: { ...mappings[idx] } }));
        // mark draft
        setMappings(prev =>
          prev.map((m,i) => i===idx ? { ...m, isDraft:true } : m)
        );
      };
      
      // -----------------------------------------------------------------------------
       // 6) Confirm (✓) the draft row’s changes
      // -----------------------------------------------------------------------------
      const confirmDraftMapping = (idx: number) => {
        const m = mappings[idx];
        if (!m.question_set_id) {
          toast({ title: "Please select a Question Set", status: "warning" });
          return;
        }
        setMappings(prev =>
          prev.map((m,i) => i===idx ? { ...m, isDraft:false } : m)
        );
        // drop its backup
        setMappingBackups(b => {
          const { [idx]:_, ...rest } = b;
          return rest;
        });
        setIsMappingEdited(true);
      };
      
      // -----------------------------------------------------------------------------
       // 7) Cancel (✕) the draft row
      // -----------------------------------------------------------------------------
      const cancelDraftMapping = (idx: number) => {
        if (mappingBackups.hasOwnProperty(idx)) {
          // existing row: restore from backup
          setMappings(prev =>
            prev.map((m,i) =>
              i===idx
                ? ({ ...mappingBackups[idx], isDraft:false })
                : m
            )
          );
          setMappingBackups(b => {
            const { [idx]:_, ...rest } = b;
            return rest;
          });
        } else {
          // brand‐new row: just drop it
          setMappings(prev =>
            prev
              .filter((_,i)=> i!==idx)
              .map((m,i)=> ({ ...m, sequence: i+1 }))
          );
        }
        setIsMappingEdited(true);
      };

    // ────────────────────────────────────────────────────────────────────────────
    //  Your existing deleteMapping + updateMapping + getAvailableQuestionSets
    // ────────────────────────────────────────────────────────────────────────────
    const deleteMapping = (idx: number) => {
    setMappings(prev =>
        prev
        .filter((_, i) => i !== idx)
        .map((m, i) => ({ ...m, sequence: i + 1 }))
    );
    setIsMappingEdited(true);
    };

    const updateMapping = (idx: number, updates: Partial<EditableMapping>) => {
    setMappings(prev =>
        prev.map((m, i) => (i === idx ? { ...m, ...updates } : m))
    );
    setIsMappingEdited(true);
    };

    const getAvailableQuestionSets = (currentIndex: number) => {
    const currentId = mappings[currentIndex]?.question_set_id;
    const taken = mappings
        .filter((_, i) => i !== currentIndex)
        .map(m => m.question_set_id);
    return questionSets.filter(
        qs =>
        !taken.includes(qs.question_set_id) ||
        qs.question_set_id === currentId
    );
    };

  
    // ────────────────────────────────────────────────────────────────────────────
    // Batch-mapping helpers
    // ────────────────────────────────────────────────────────────────────────────
    const batchAvailableOptions = batches.filter(
        (b) => !batchMappedList.some((mapped) => mapped.id === b.id)
      );
      
      const handleAddBatchesToExam = () => {
        const newBatches = batchAvailableOptions.filter((b) =>
          batchSelectedIds.includes(b.id)
        );
        setBatchMappedList((prev) => [...prev, ...newBatches]);
        setBatchSelectedIds([]);
        setBatchIsMappingEdited(true);
      };

    const handleRemoveBatch = (batchId: number) => {
      setBatchMappedList(prev => prev.filter(b => b.id !== batchId));
      setBatchIsMappingEdited(true);
    };
  
    // ────────────────────────────────────────────────────────────────────────────
    // Form handlers
    // ────────────────────────────────────────────────────────────────────────────
    const handleInputChange = (e: React.ChangeEvent<any>) => {
      const { name, value } = e.target;
      setExam(prev => ({ ...prev, [name]: value }));
      setIsExamEdited(true);
    };
  
    // ────────────────────────────────────────────────────────────────────────────
    // Save / Create / Update / Publish / Cancel / Terminate
    // ────────────────────────────────────────────────────────────────────────────
    const handleCreateExam = async () => {
      try {
        const api = process.env.REACT_APP_API_URL || 'https://default-url.com';

        const payload = {
          ...exam,
          start_time: toBackendISO(exam.start_time),  // "YYYY-MM-DDTHH:mm:ss.SSS"
          end_time  : toBackendISO(exam.end_time),
          // add any other Date fields you send:
          published_at        : exam.published_at        && toBackendISO(exam.published_at),
          results_published_on: exam.results_published_on&& toBackendISO(exam.results_published_on),
          results_finalized_on: exam.results_finalized_on&& toBackendISO(exam.results_finalized_on),
        };

        const { data } = await axios.post<Exam>(`${api}/create-exam/`, payload, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        const newId = data.exam_id;
        // re-map question-sets
        if (isMappingEdited) {
          await axios.delete(`${api}/delete-exam-mappings/${newId}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          });
          /** Strip UI-only props and fix the date */
            const payload = mappings.map(({ isDraft, ...m }) => ({
              ...m,
              start_time: toBackendISO(m.start_time),      // "YYYY-MM-DDTHH:mm:ss.SSS"
            }));
          await axios.post(`${api}/create-exam-mapping/${newId}`, payload, {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          });
        }
        // re-map batches
        if (batchIsMappingEdited) {
          await axios.delete(`${api}/delete-exam-batches/${newId}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          });
          const batchPayload = batchMappedList.map(b => ({ exam_id: newId, batch_id: b.id }));
          await axios.post(`${api}/create-exam-batches/${newId}`, batchPayload, {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          });
        }
        toast({ title: "Exam created", status: "success" });
        setIsMappingEdited(false);
        setBatchIsMappingEdited(false);
        setIsExamEdited(false);
       // navigate(`/coaching/admin/dashboard/exam-manager/review-exam/${newId}`);
        if (tenant)
        {
          navigate(`/platform/${tenant.tenant_slug}/app/exam-manager/review-exam/${newId}`);
        }
      } catch (err) {
        console.error(err);
        toast({ title: "Error creating exam", status: "error" });
      }
    };
  
    const handleUpdateExam = async () => {
      const api = process.env.REACT_APP_API_URL || 'https://default-url.com';
      // guard
      if (!["DRAFT", "PUBLISHED"].includes(exam.status)) {
        toast({ title: "Cannot update", status: "warning" });
        return;
      }
      try {

         const payload = {
          ...exam,
          start_time: toBackendISO(exam.start_time),  // "YYYY-MM-DDTHH:mm:ss.SSS"
          end_time  : toBackendISO(exam.end_time),
          // add any other Date fields you send:
          published_at        : exam.published_at        && toBackendISO(exam.published_at),
          results_published_on: exam.results_published_on&& toBackendISO(exam.results_published_on),
          results_finalized_on: exam.results_finalized_on&& toBackendISO(exam.results_finalized_on),
        };

        await axios.put(`${api}/update-exam/${exam_id}`, payload, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        if (isDraft && isMappingEdited) {
          await axios.delete(`${api}/delete-exam-mappings/${exam_id}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          });
                    /** Strip UI-only props and fix the date */
            const payload = mappings.map(({ isDraft, ...m }) => ({
              ...m,
              start_time: toBackendISO(m.start_time),      // "YYYY-MM-DDTHH:mm:ss.SSS"
            }));
          await axios.post(`${api}/create-exam-mapping/${exam_id}`, payload, {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          });
        }
        if (isDraft && batchIsMappingEdited) {
          await axios.delete(`${api}/delete-exam-batches/${exam_id}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          });
          const batchPayload = batchMappedList.map(b => ({ exam_id: Number(exam_id), batch_id: b.id }));
          await axios.post(`${api}/create-exam-batches/${exam_id}`, batchPayload, {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          });
        }
        if (isPublished && isMappingEdited) {
          // only remap dates
          const payload = mappings.map(m => ({
            question_set_id: m.question_set_id,
            start_time: toBackendISO(m.start_time),  
          }));
          console.log("Question Set Mappings Sent for Update",payload);
          await axios.put(`${api}/update-exam-mapping-dates/${exam_id}`, payload, {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          });
        }
        toast({ title: "Exam updated", status: "success" });
        setIsMappingEdited(false);
        setBatchIsMappingEdited(false);
        setIsExamEdited(false);
      } catch (err) {
        console.error(err);
        toast({ title: "Error updating exam", status: "error" });
      }
    };
  
    const handleSaveExam = () => {
      isCreating ? handleCreateExam() : handleUpdateExam();
      setIsEditing(false);
    };
  
    const handleUpdateClick = async () => {
        setIsEditing(true);
    }
    const handlePublishExam = async () => { 

        const api = process.env.REACT_APP_API_URL || 'https://default-url.com';
        // guard
        if (["CANCELLED", "PUBLISHED", "ONGOING", "TERMINATED"].includes(exam.status)) {
            toast({ title: "Cannot update", status: "warning" });
            return;
        }
        try {
            await axios.put(`${api}/publish-exam/${exam_id}`, exam, {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
            });
            toast({ title: "Exam Published", status: "success" });
            loadAllData();
        } catch (err) {
            console.error(err);
            toast({ title: "Error Publishing exam", status: "error" });
      }
    };

    const handleCancelExam  = async () => { 

        const api = process.env.REACT_APP_API_URL || 'https://default-url.com';
        // guard
        if (["DRAFT", "CANCELLED", "ONGOING", "TERMINATED"].includes(exam.status)) {
            toast({ title: "Cannot update", status: "warning" });
            return;
        }
        try {
            await axios.put(`${api}/cancel-exam/${exam_id}`, exam, {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
            });
            toast({ title: "Exam Cancelled", status: "success" });
            loadAllData();
        } catch (err) {
            console.error(err);
            toast({ title: "Error Cancelling exam", status: "error" });
      }
    };
    const handleTerminateExam = async () => { 

        const api = process.env.REACT_APP_API_URL || 'https://default-url.com';
        // guard
        if (["CANCELLED", "PUBLISHED", "TERMINATE", "DRAFT"].includes(exam.status)) {
            toast({ title: "Cannot update", status: "warning" });
            return;
        }
        try {
            await axios.put(`${api}/terminate-exam/${exam_id}`, exam, {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
            });
            toast({ title: "Exam Terminated", status: "success" });
            loadAllData();
        } catch (err) {
            console.error(err);
            toast({ title: "Error Terminating exam", status: "error" });
      }
    };

    const handleResultsPublishedExam = async () => { 

        const api = process.env.REACT_APP_API_URL || 'https://default-url.com';
        // guard
        if (["CANCELLED", "PUBLISHED", "TERMINATE", "DRAFT", "ONGOING", "COMPLETED"].includes(exam.status)) {
            toast({ title: "Cannot update", status: "warning" });
            return;
        }
        try {
            await axios.put(`${api}/results-published-exam/${exam_id}`, exam, {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
            });
            toast({ title: "Results Successfully Published", status: "success" });
            loadAllData();
        } catch (err) {
            console.error(err);
            toast({ title: "Error Publishing Results", status: "error" });
      }
    };

        const handleFinalizeResultsExam = async () => { 

        const api = process.env.REACT_APP_API_URL || 'https://default-url.com';
        // guard
        if (exam.status!= "RESULTS_PUBLISHED") {
            toast({ title: "Cannot update", status: "warning" });
            return;
        }
        try {
            await axios.put(`${api}/results-finalized-exam/${exam_id}`, exam, {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
            });
            toast({ title: "Results Successfully Published", status: "success" });
            loadAllData();
        } catch (err) {
            console.error(err);
            toast({ title: "Error Publishing Results", status: "error" });
      }
    };


    const handleScoreExam = async () => { 

        const api = process.env.REACT_APP_API_URL || 'https://default-url.com';
        // guard
        if (["CANCELLED", "PUBLISHED", "TERMINATE", "DRAFT", "ONGOING"].includes(exam.status)) {
            toast({ title: "Cannot update", status: "warning" });
            return;
        }
        try {
        const api = process.env.REACT_APP_API_URL || 'https://default-url.com';
        const { data } = await axios.post<ExamAttemptScoreStatusByTutor[]>(`${api}/exam/scoring/execute/${exam_id}`, exam, {  //axios.post(`${api}/scoring/execute/${exam_id}`, exam, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        setExamAttemptScoreStatusByTutor(data);
            toast({ title: "Scoring Engine Fired Successfully", status: "success" });
            loadAllData();
        } catch (err) {
            console.error(err);
            toast({ title: "Error Firing-up the Scoring Engine", status: "error" });
      }
    };

    const fetchExamAttemptStatusByTutor = async () => { 

        // guard
        if (["CANCELLED", "PUBLISHED", "TERMINATE", "DRAFT", "ONGOING"].includes(exam.status)) {
            toast({ title: "Cannot update", status: "warning" });
            return;
        }
        try {
        const api = process.env.REACT_APP_API_URL || 'https://default-url.com';

        const { data } = await axios.get<ExamAttemptScoreStatusByTutor[]>(`${api}/exam/scoring/status/${exam_id}`, {  
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        setExamAttemptScoreStatusByTutor(data);
            loadAllData();
        } catch (err) {
            console.error(err);
            toast({ title: "Error Fetching Exam Attempt", status: "error" });
      }
    };

    const refreshTutorMapping = async () => { 
      try {
              const api = process.env.REACT_APP_API_URL || 'https://default-url.com';

                await axios.put(`${api}/exam/update-attempt-tutor-mapping/${exam_id}`, exam, {
                  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
                });
                toast({ title: "Exam Attempt - Tutor Mappings Refreshed Successfully", status: "success" });
                fetchExamAttemptStatusByTutor();
                //  loadAllData();
              } catch (err) {
                  console.error(err);
                  toast({ title: "Error Firing-up the Scoring Engine", status: "error" });
            }
    };

    const generateAnalytics = async () => { 

        const api = process.env.REACT_APP_API_URL || 'https://default-url.com';
        // guard
        if (["CANCELLED", "PUBLISHED", "TERMINATE", "DRAFT", "ONGOING"].includes(exam.status)) {
            toast({ title: "Cannot update", status: "warning" });
            return;
        }
        try {
        const api = process.env.REACT_APP_API_URL || 'https://default-url.com';
        const { data } = await axios.post(`${api}/exam/analytics/execute/${exam_id}`, exam, {  //axios.post(`${api}/scoring/execute/${exam_id}`, exam, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
            toast({ title: "Analytics Generated Successfully", status: "success" });
            loadAllData();
        } catch (err) {
            console.error(err);
            toast({ title: "Error Generateing Analytics", status: "error" });
      }
    };

    const handleCancelOperation = () =>{
        setIsEditing(false);  
         loadAllData();
        };

    const handleOverride = (examAttemptId: number) => {
    //  navigate(`/coaching/admin/dashboard/exam-manager/review-exam/${exam_id}/override/${examAttemptId}`);

      if (tenant)
      {
        navigate(`/platform/${tenant.tenant_slug}/app/exam-manager/review-exam/${exam_id}/override/${examAttemptId}`);
      }
      
    };

    const handleViewAttempt = (examAttemptId: number) => {
   //   navigate(`/coaching/admin/dashboard/exam-manager/review-exam/${exam_id}/score/${examAttemptId}`);

      if (tenant)
      {
        navigate(`/platform/${tenant.tenant_slug}/app/exam-manager/review-exam/${exam_id}/score/${examAttemptId}`);
      }
      
    };

    /* --- tiny helpers just for readability --- */
    const fmtDate = (ts: string | Date) =>
      parseUtc(ts).toLocaleDateString(undefined, {
        day:   "2-digit",
        month: "short",
        year:  "numeric",
      });

    const fmtTime = (ts: string | Date) =>
      parseUtc(ts).toLocaleTimeString(undefined, {
        hour:   "2-digit",
        minute: "2-digit",
      });

    const minutesBetween = (a: string | Date, b: string | Date) =>
      Math.round((parseUtc(b).getTime() - parseUtc(a).getTime()) / 60_000);


    // 🔹 at the top of ReviewExam component
    const [startDraftById, setStartDraftById] = React.useState<Record<number, string>>({});

    const handleStartChange = (rowIndex: number, raw: string) => {
      // always mirror what the user typed
      setStartDraftById(prev => ({ ...prev, [rowIndex]: raw }));

      // commit only if it's a valid datetime
      const d = new Date(raw);
      if (!Number.isNaN(d.getTime())) {
        updateMapping(rowIndex, { start_time: d });
      }
    };

    const clearStartDraft = (rowIndex: number) => {
      setStartDraftById(prev => {
        const { [rowIndex]: _, ...rest } = prev;
        return rest;
      });
    };

  return (
    <Box as="main" mx="auto" p={10} >

 {/* Exam Details Top Bar */}
    <Box bg={useColorModeValue("gray.100", "gray.700")} p={4} borderRadius="sm" mb={4}>
    <Flex direction={["column", "row"]} justify="space-between" align={["flex-start", "center"]} wrap="wrap">
        <Heading size="lg">{exam.title || "Untitled Exam"}</Heading>
        <HStack spacing={2} mt={[2, 0]}>
          <Badge
            colorScheme={
              exam.status === "DRAFT" ? "gray" :
              exam.status === "PUBLISHED" ? "blue" :
              exam.status === "ONGOING" ? "green" :
              exam.status === "COMPLETED" ? "purple" :
              exam.status === "FULLY_SCORED" ? "teal" :
              exam.status === "CANCELLED" ? "red" :
              "orange"
            }
          >
            {exam.status === "FULLY_SCORED"
              ? `FULLY SCORED${exam.is_analytics_generated ? " WITH ANALYTICS" : ""}`
              : exam.status.replace(/_/g, " ")}
          </Badge>
        </HStack>
    </Flex>

{/*     <Flex mt={3} wrap="wrap" fontSize="sm" color={useColorModeValue("gray.600", "gray.400")}>
        <Text mr={4}><strong>Start Date:</strong> {new Date(exam.start_time).toLocaleDateString()}</Text>
        <Text mr={4}><strong>Start Time:</strong> {new Date(exam.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</Text>
        <Text mr={4}><strong>Duration:</strong> {
        Math.round(
            (new Date(exam.end_time).getTime() - new Date(exam.start_time).getTime()) / 60000
        )
        } min</Text>
        {exam.published_at && (
        <Text mr={4}><strong>Published:</strong> {new Date(exam.published_at).toLocaleDateString()}</Text>
        )}
        <Text mr={4}><strong>Created:</strong> {new Date(exam.created_at).toLocaleDateString()}</Text>
        <Text><strong>Updated:</strong> {new Date(exam.updated_at).toLocaleDateString()}</Text>
    </Flex> */}

    <Flex
      mt={3}
      wrap="wrap"
      fontSize="sm"
      color={useColorModeValue("gray.600", "gray.400")}
    >
      {/* Start date (local) */}
      <Text mr={4}>
        <strong>Start Date & Time:</strong>{" "}
       {fmtLocal(exam.start_time)}
      </Text>

      {/* Start time (local, hh:mm) */}
      <Text mr={4}>
        <strong>Start Time:</strong>{" "}
        {parseUtc(exam.start_time).toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        })}
      </Text>

      {/* Paper duration in minutes */}
      <Text mr={4}>
        <strong>Duration:</strong>{" "}
        {Math.round(
          (parseUtc(exam.end_time).getTime() -
            parseUtc(exam.start_time).getTime()) /
            60_000,
        )}{" "}
        min
      </Text>

      {/* Published date (if any) */}
      {exam.published_at && (
        <Text mr={4}>
          <strong>Published:</strong>{" "}
          {fmtLocal(exam.published_at, { hour: undefined, minute: undefined })}
        </Text>
      )}

      {/* Created / updated */}
      <Text mr={4}>
        <strong>Created:</strong>{" "}
        {fmtLocal(exam.created_at, { hour: undefined, minute: undefined })}
      </Text>

      <Text>
        <strong>Updated:</strong>{" "}
        {fmtLocal(exam.updated_at, { hour: undefined, minute: undefined })}
      </Text>
    </Flex>

    </Box>

{/* Operations Buttons */}
    <Box mb={4}>
    {showActionButtons && (
        <HStack spacing={2} mt={4}>
            <Button
            size="sm"
            borderRadius="sm"
            onClick={handleUpdateClick}
            isDisabled={!isUpdateButtonEnabled}
            colorScheme="blue"
            >
            Update Exam
            </Button>
            <Button
            size="sm"
            borderRadius="sm"
            onClick={() => {
                if (window.confirm("Are you sure you want to Publish this Exam?")) {
                    handlePublishExam()};
                }
            }
            isDisabled={!isPublishButtonEnabled}
            colorScheme="green"
            >
            Publish Exam
            </Button>

            <Button
            size="sm"
            borderRadius="sm"
            onClick={() => {
                if (window.confirm("Are you sure you want to Cancel this Exam?")) {
                    handleCancelExam()};
                }
            }
            isDisabled={!isCancelButtonEnabled}
            colorScheme="orange"
            >
            Cancel Exam
            </Button>

            <Button
            size="sm"
            borderRadius="sm"
            onClick={() => {
                if (window.confirm("Are you sure you want to Terminate this Exam?")) {
                    handleTerminateExam()};
                }
            }
            isDisabled={!isTerminateButtonEnabled}
            colorScheme="red"
            >
            Terminate Exam
            </Button>

            <Button
            size="sm"
            borderRadius="sm"
            onClick={() => {
                if (window.confirm("Are you sure you want to Fire the Scoring Engine for this Exam?")) {
                    handleScoreExam()};
                }
            }
            isDisabled={!isScoringButtonEnabled}
            colorScheme="cyan"
            >
            Fire Scoring Engine
            </Button>

            <Button
            size="sm"
            borderRadius="sm"
            onClick={() => {
                if (window.confirm("Are you sure you want to Publish the results for this Exam?")) {
                    handleResultsPublishedExam()};
                }
            }
            isDisabled={!isPublishResultsButtonEnabled}
            colorScheme="purple"
            >
            Publish Results
            </Button>

                        <Button
            size="sm"
            borderRadius="sm"
            onClick={() => {
                if (window.confirm("Are you sure you want to Finalize the results for this Exam?")) {
                    handleFinalizeResultsExam()};
                }
            }
            isDisabled={!isFinalizeResultsButtonEnabled}
            colorScheme="pink"
            >
            Finalize Results
            </Button>

          {isEditing && (
            <Button size={"sm"} variant="outline" colorScheme="red" onClick={handleCancelOperation}>
              Cancel
            </Button>
          )}

        </HStack>
        )}
    </Box>

  {!(isInScoring || isFullyScored || isResultsPublished || isResultsFinalized ) && (
    <Box >

        {/* Exam Title & Description & Settings */}
        <Box mt={4} bgColor={cardbg} borderRadius="sm" p={4} boxShadow="md">
          <fieldset disabled={!showSaveButton}>
            <Text mb={4} fontSize="md" fontWeight="bold">Title, Description & Settings</Text>
            <VStack spacing={4} align="stretch">
              <FormControl id="title" isRequired>
                <Input
                  name="title"
                  placeholder="Exam Title*"
                  size="sm"
                  value={exam.title}
                  onChange={handleInputChange}
                />
              </FormControl>

              <FormControl id="description" isRequired>
                <Input
                  name="description"
                  placeholder="Exam Description*"
                  size="sm"
                  value={exam.description}
                  onChange={handleInputChange}
                />
              </FormControl>

              <FormControl id="passing_percentage" isRequired>
                <Input
                  name="passing_percentage"
                  type="number"
                  step="0.1"
                  placeholder="Passing Percentage*"
                  size="sm"
                  value={exam.passing_percentage}
                  onChange={handleInputChange}
                />
              </FormControl>

              <FormControl id="max_view_limit" isRequired>
                <Input
                  name="max_view_limit"
                  type="number"
                  placeholder="Max View Limit*"
                  size="sm"
                  value={exam.max_view_limit}
                  onChange={handleInputChange}
                />
              </FormControl>

                <FormControl id="result_challenge_days" isRequired>
                <Input
                  name="result_challenge_days"
                  type="number"
                  placeholder="Result Challenge Days*"
                  size="sm"
                  value={exam.result_challenge_days}
                  onChange={handleInputChange}
                />
              </FormControl>

              <FormControl display="flex" alignItems="center">
                <Checkbox
                  name="instant_result_enabled"
                  isChecked={exam.instant_result_enabled}
                  onChange={(e) => {
                    setExam(prev => ({
                      ...prev,
                      instant_result_enabled: e.target.checked,
                    }));
                    setIsExamEdited(true);
                  }}
                >
                  Instant Result Enabled
                </Checkbox>
              </FormControl>
            </VStack>
          </fieldset>
        </Box>



        {/* Exam Question Set Mapping */}
        <Box mt={4} bgColor={cardbg} borderRadius="sm" p={4} boxShadow="md">
        <fieldset disabled={!showSaveButton}>
        <Text mb={4} fontSize="md" fontWeight="bold">Question Sets</Text>
        <Table variant="simple" size="sm">
            <Thead>
                <Tr>
                <Th>Seq</Th>
                <Th>Question Set</Th>
                <Th>Category</Th>
                <Th>Duration (min)</Th>
                <Th>Start Time</Th>
                <Th>Actions</Th>
                </Tr>
            </Thead>
            <Tbody>
                {mappings.map((mapping, index) => {
                const selectedQSet = questionSets.find(
                    (qs) => qs.question_set_id === mapping.question_set_id
                );

                const selectedCategory = questionSetCategories.find(
                    (cat) => cat.category_id === selectedQSet?.category_id
                );

                    // buffer: prefer draft if editing, else canonical
                const buffer = startDraftById[index] ?? utcToInputValue(mapping.start_time);

                return (
                    <Tr key={index}>
                      <Td>{mapping.sequence}</Td>
                      <Td>
                        <ChakraSelect
                          size="sm"
                          placeholder="Select question set"
                          value={mapping.question_set_id || ''}
                          isDisabled={ isPublished || !mapping.isDraft }
                          onChange={e =>
                            updateMapping(index, {
                              question_set_id: Number(e.target.value)
                            })
                          }
                        >
                          {getAvailableQuestionSets(index).map(qs => (
                            <option
                              key={qs.question_set_id}
                              value={qs.question_set_id}
                            >
                              {qs.title}
                            </option>
                          ))}
                        </ChakraSelect>
                      </Td>
                      <Td>{selectedCategory?.category_name || '–'}</Td>
                      <Td>{selectedQSet?.duration ?? '–'}</Td>
{/*                       <Td>
                        <Input
                          size="sm"
                          type="datetime-local"
                          value={new Date(
                            new Date(mapping.start_time).getTime() -
                              new Date().getTimezoneOffset() * 60000
                          )
                            .toISOString()
                            .slice(0, 16)}
                          isDisabled={ !mapping.isDraft }
                          onChange={e =>
                            updateMapping(index, { start_time: new Date(e.target.value) })
                          }
                        />
                      </Td> */}
                      <Td>
{/*                       <Input
                          size="sm"
                          type="datetime-local"
                          value={utcToInputValue(mapping.start_time)}
                          isDisabled={!mapping.isDraft}
                          onChange={e =>
                            updateMapping(index, {
                              start_time: parseUtc(inputValueToUtc(e.target.value)),  // string ➜ Date
                            })
                          }
                        /> */}
{/*                         <Input
                            size="sm"
                            type="datetime-local"
                            value={utcToInputValue(mapping.start_time)}      // shows local time
                            isDisabled={!mapping.isDraft}
                            onChange={e => {
                              // 1️⃣ keep the tutor-picked wall-clock as a Date
                              const localDate = new Date(e.target.value);    // 11:00 local → Date(11:00 local)

                              // 2️⃣ immutably update the right mapping row
                              updateMapping(index, { start_time: localDate });
                            }}
                          /> */}
                          <Input
                            size="sm"
                            type="datetime-local"
                            step="60"
                            value={buffer}
                            isDisabled={!mapping.isDraft}
                            onChange={(e) => handleStartChange(index, e.target.value)}
                            onBlur={() => clearStartDraft(index)}
                          />
                        </Td>
                      <Td>
                        {mapping.isDraft ? (
                          <HStack spacing={1}>
                            <IconButton
                              aria-label="Confirm"
                              icon={<FiCheck />}
                              size="sm"
                              colorScheme="green"
                              isDisabled={!mapping.question_set_id}
                              onClick={() => confirmDraftMapping(index)}
                            />
                            <IconButton
                              aria-label="Cancel"
                              icon={<FiX />}
                              size="sm"
                              colorScheme="red"
                              onClick={() => cancelDraftMapping(index)}
                            />
                          </HStack>
                        ) : (
                          <HStack spacing={1}>
                            <IconButton
                              aria-label="Edit"
                              icon={<FiEdit />}
                              colorScheme="blue"
                              size="sm"
                              onClick={() => editMapping(index)}
                              isDisabled={hasDraftMappingRow()}
                            />
                            <IconButton
                              aria-label="Delete"
                              icon={<FiTrash />}
                              size="sm"
                              colorScheme="red"
                            //   onClick={() => deleteMapping(index)}
                              onClick={() => {
                                if (window.confirm("Are you sure you want to remove this Question Set?")) {
                                    deleteMapping(index)};
                                }
                            }
                              isDisabled={hasDraftMappingRow() || isPublished}
                            />
                          </HStack>
                        )}
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>

                <Button
                    onClick={addMapping}
                    size="sm"
                    borderRadius="sm"
                    colorScheme="blue"
                    mt={2}
                    isDisabled={
                        hasDraftMappingRow() ||
                        mappings.length >= questionSets.length ||
                        isPublished
                    }
                    >
                    + Q Set
                    </Button>


                </fieldset>

        </Box>

        {/* Exam Batch Mapping */}  
        <Box
        mt={4}
        bg={cardbg}
        p={4}
        borderRadius="sm"
        boxShadow="md"
        >
        <fieldset disabled={!showSaveButton || isPublished}>
        <Text mb={4} fontSize="md" fontWeight="bold">
            Batches
        </Text>
        <VStack spacing={4} align="stretch">
            {/* Batch Multi-Select Dropdown and Add Button */}
        <Box>
        <ReactSelect
            isMulti
            options={batchAvailableOptions.map((b) => ({
            label: `${b.title} | ${b.course_title} | ${b.tutor_name}`,
            value: b.id,
            }))}
            value={batchAvailableOptions
            .filter((b) => batchSelectedIds.includes(b.id))
            .map((b) => ({
                label: `${b.title} | ${b.course_title} | ${b.tutor_name}`,
                value: b.id,
            }))}
            onChange={(selectedOptions: readonly { label: string; value: number }[] | null) => {
            if (selectedOptions) {
                setBatchSelectedIds(selectedOptions.map((opt) => opt.value));
            } else {
                setBatchSelectedIds([]);
            }
            }}
            placeholder="Select batches to add..."
            closeMenuOnSelect={false}
            isDisabled={batchAvailableOptions.length === 0}
        />
        <Button
            size="sm"
            mt={6}
            onClick={handleAddBatchesToExam}
            isDisabled={batchAvailableOptions.length === 0}
            colorScheme="blue"
            borderRadius={"sm"} fontSize="sm"
        >
            Add Batches to Exam
        </Button>
        </Box>
            {/* Table of Mapped Batches */}
            <Box>
            <Table size="sm">
                <Thead>
                <Tr>
                    <Th>Batch</Th>
                    <Th>Course</Th>
                    <Th>Tutor</Th>
                    <Th></Th>
                </Tr>
                </Thead>
                <Tbody>
                {batchMappedList.map((batch) => (
                    <Tr key={batch.id}>
                    <Td>{batch.title}</Td>
                    <Td>{batch.course_title}</Td>
                    <Td>{batch.tutor_name}</Td>
                    <Td>
                        <Button
                        size="sm"
                        aria-label="Delete"
                        colorScheme="red"
                        onClick={() => {
                            if (window.confirm("Are you sure you want to remove this batch?")) {
                                handleRemoveBatch(batch.id);
                            }
                            }}
                        >
                            <FiTrash />
                        </Button>
                    
                    </Td>
                    </Tr>
                ))}
                </Tbody>
            </Table>
            </Box>
        </VStack>
        </fieldset>
        </Box>

        {/* Save Button */}
        {showSaveButton && (
            <Box textAlign="left" mt={4}>
                <Button
                mb={2}
                colorScheme="teal"
                size="sm"
                borderRadius="sm"
                onClick={handleSaveExam}
                isDisabled={hasDraftMappingRow()||!(isExamEdited||isMappingEdited||batchIsMappingEdited)}
                >
                Save Exam
                </Button>
                
            </Box>
            )}
        </Box>
        )}

    {(isInScoring || isFullyScored || isResultsPublished) && !isResultsFinalized && (
        <Box
          borderRadius="sm"
          boxShadow="md"
          border="1px solid"
          borderColor="gray.200"
          p={2}
        >
        <HStack
          justify="space-between"
          align="center"
          borderWidth="1px"
          borderRadius="md"
          px={4}
          py={3}
          mb={4}
          bg={cardbg}
          spacing={4}
        >

          {/* Left content expands to push buttons right */}
          <Box flex="1">
            <VStack spacing={2} align="start">
              <HStack>
                <EditIcon color="purple.500" boxSize={4} />
                <Text fontSize="sm" fontWeight="semibold">
                  Manual Scoring Remaining Attempts: {exam.manual_scoring_pending_attempts_count}
                </Text>
              </HStack>
              <HStack>
                <CheckCircleIcon color="green.500" boxSize={4} />
                <Text fontSize="sm" fontWeight="semibold">
                  Auto Scoring Remaining Attempts: {exam.auto_scoring_pending_attempts_count}
                </Text>
              </HStack>
            </VStack>
          </Box>

          {/* Buttons on the right */}
            <Button
            size="sm"
            borderRadius="sm"
            colorScheme="cyan"
            onClick={generateAnalytics}
            isDisabled={!isGenerateAnalyticsButtonEnabled}
          >
            Generate Analytics
          </Button>
          <Button
            size="sm"
            borderRadius="sm"
            colorScheme="blue"
            onClick={refreshTutorMapping}
            isDisabled={!isExamAttemptStatusButtonEnabled}
          >
            Refresh Tutor Mapping
          </Button>

          <Button
            size="sm"
            borderRadius="sm"
            colorScheme="yellow"
            onClick={fetchExamAttemptStatusByTutor}
            isDisabled={!isExamAttemptStatusButtonEnabled}
          >
            Refresh Attempt Status
          </Button>
        </HStack>
                
        < ExamAttemptScoringStatus
            examAttemptScoringStatuses={examAttemptScoreStatusByTutor}
            onOverride={handleOverride}
            onViewAttempt={handleViewAttempt}
          />
        </Box>
        )}


      {exam.status === "RESULTS_FINALIZED" && exam.results_finalized_on && (
      <Box flex="1">
        <VStack spacing={2} align="start">
          <HStack>
            <CheckCircleIcon color="blue.500" boxSize={4} />
            <Text fontSize="sm" fontWeight="semibold">
              Results Finalized On:&nbsp;
              <Text as="span" color="gray.700">
            {/*     {new Date(exam.results_finalized_on).toLocaleDateString()} */}
                      {fmtLocal(exam.results_finalized_on, {
                      day:   "2-digit",
                        month: "short",
                        year:  "numeric",
                        hour:  undefined,
                        minute: undefined,
                      })}
              </Text>
            </Text>
          </HStack>
        </VStack>
      </Box>
    )}

    </Box>
      
  );
};

export default ReviewB2CExam;
